import { ClientFactory, type SubjectSuggestion } from "./ClientFactory";

export type { SubjectSuggestion };

/**
 * Debounce matching {@code UsernameSuggestionField}'s existing behavior, so the two async
 * typeaheads in the product feel the same.
 */
export const SUBJECT_SUGGESTION_DEBOUNCE_MS = 220;

/** Below this, a query matches too much to be useful and we skip the round trip entirely. */
export const SUBJECT_SUGGESTION_MIN_CHARS = 2;

/** Server clamps to 10; asking for more is silently capped, so ask for what we render. */
export const SUBJECT_SUGGESTION_LIMIT = 8;

/**
 * Whole half-stars → a display number. Kept here rather than in the component so the post page,
 * the subject page, and the combobox cannot disagree about what "★4.5" means.
 */
export function halfStarsToStars(halfStars: number | null | undefined): number | null {
    if (halfStars === null || halfStars === undefined) {
        return null;
    }
    return halfStars / 2;
}

/**
 * Fetches subject suggestions for the review facet's typeahead.
 *
 * <p>Returns `[]` rather than throwing when the query is too short, so callers can call it
 * unconditionally on every keystroke. A cancelled request (superseded keystroke) rethrows, because
 * the caller needs to distinguish "aborted" from "no results" — showing an empty list for an
 * aborted request produces a visible flash of "Nothing found yet".
 */
export async function fetchSubjectSuggestions(
    searchQuery: string,
    groupPublicId?: string,
    signal?: AbortSignal,
): Promise<SubjectSuggestion[]> {
    const trimmed = searchQuery.trim();
    if (trimmed.length < SUBJECT_SUGGESTION_MIN_CHARS) {
        return [];
    }

    const response = await ClientFactory.getSubjectSuggestions(
        trimmed,
        groupPublicId,
        SUBJECT_SUGGESTION_LIMIT,
        signal ? { signal } : undefined,
    );
    return response.data ?? [];
}

/**
 * The "did you mean" test (§4.3 layer 2): does a suggestion effectively duplicate what the author
 * just typed?
 *
 * <p>Mirrors the server's `tightenName` — lower-cased, alphanumerics only. This is the ONLY place
 * the frontend derives a matching key, and it exists so the "did you mean" row can be shown without
 * a second round trip. It must stay in step with `SubjectService.tightenName`; if the two drift, the
 * row stops appearing and duplicates start being created silently, which is exactly the failure the
 * row exists to prevent.
 */
export function tightenSubjectName(name: string): string {
    return name
        .normalize("NFKC")
        .toLowerCase()
        .replace(/[^\p{L}\p{N}]/gu, "");
}

/**
 * The suggestion that should be surfaced as "did you mean" directly above *Add "…"*, or null when
 * the typed name is genuinely new.
 *
 * <p>Only fires when the typed name does NOT exactly match a suggestion's display name — if it
 * already matches exactly, the suggestion is in the list above and a "did you mean" would be noise.
 */
export function findNearMatch(
    query: string,
    suggestions: SubjectSuggestion[],
): SubjectSuggestion | null {
    const tightQuery = tightenSubjectName(query);
    if (!tightQuery) {
        return null;
    }
    const exactDisplayMatch = suggestions.some(
        (suggestion) => suggestion.name.trim().toLowerCase() === query.trim().toLowerCase(),
    );
    if (exactDisplayMatch) {
        return null;
    }
    return suggestions.find((suggestion) => tightenSubjectName(suggestion.name) === tightQuery) ?? null;
}
